/**
 * Bottom status ticker. Weather, city clock, the Beat, and the utility tray.
 *
 * **THE TWO ROLLING FEEDS ARE GONE.** They were six hand-written headlines rotating every seven
 * seconds "so the world reads as running even when the player is idle", and three of the six
 * stated things about the world that are false. A rolling feed of fabricated events beside a live
 * clock is the most convincing lie on a screen, because motion is what stops a reader checking.
 *
 * What replaces them is the one thing in the city that genuinely moves on its own: the Beat, a
 * pure function of the clock over the 2,016-entry schedule published at Era open. The real feed
 * is the Scanner's forty authored dispatch lines, which the server already renders on rotation;
 * publishing them to a client feed is Phase 9.
 *
 * `compact` is the phone: the clock and the Beat line only, above the tab bar. The weather, the
 * location and the tray go, because the tray's two destinations are both on the tab bar already
 * and a phone has no room for a line that says "Ashport, USA".
 */

import { Icon } from '../ui/Icon'
import { Row } from '../ui/Kit'
import type { Route } from './routes'
import { districtName, useGame } from '../game/state'
import { sz } from '../ui/viewport'

/**
 * THE UTILITY TRAY, WHICH WAS FOUR BUTTONS AND FOUR DEAD ENDS.
 *
 * `trophy`, `mail`, `gear` and `help`, none with an `onClick`, and the mail icon wearing a
 * hardcoded red `3` for messages that do not exist. Two survive with a destination and two are
 * deleted, on the rule this project already applies to mock data: a control with no system behind
 * it teaches a player that the controls are decorative.
 *
 * **`help` GOES TO HOME AND THAT IS THE WHOLE ANSWER.** Deleting it was the other candidate and
 * the evidence cuts against both alternatives: Andersen et al. found an always-available on-demand
 * help button was associated with a small REDUCTION in return rate, around 3.5 percent, so a help
 * MODAL is not the fix; and a confused player pressing the most obvious thing on the screen and
 * getting nothing is worse than either. What this game's answer to "I am lost" actually is, is the
 * panel on Home that says what to do next, so the button goes there. DESIGN-DELTAS 1t.4 and 1t.11.
 *
 * `mail` and `gear` are deleted. There is no in-game mail reader and no settings screen, and there
 * is nothing to point them at that is not a lie.
 */
const TRAY: { n: 'trophy' | 'help'; to: Route; title: string }[] = [
  { n: 'trophy', to: 'ledger', title: 'Your record' },
  { n: 'help', to: 'home', title: 'What to do next' },
]

export function Ticker({ now, go, compact = false }: { now: number; go: (r: Route) => void; compact?: boolean }) {
  const { beat } = useGame()
  const hot = districtName(beat.district)

  const time = new Date(now).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  return (
    <footer
      style={{
        height: compact ? 32 : 'var(--ftr)',
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 10 : 14,
        padding: compact ? '0 10px' : '0 14px',
        borderTop: '1px solid var(--line)',
        background: 'linear-gradient(180deg, rgba(14,19,23,.94), rgba(5,7,9,.98))',
        fontSize: 11.5,
        color: 'var(--ink-mid)',
        minWidth: 0,
      }}
    >
      {!compact && (
        <>
          <Row gap={5}>
            <Icon n="pin" s={12} c="var(--ink-dim)" />
            <span className="t-label" style={{ fontSize: sz(9.5) }}>Ashport, USA</span>
          </Row>
          <Row gap={5}>
            <Icon n="rain" s={13} c="var(--police)" />
            <span className="t-label" style={{ fontSize: sz(9.5) }}>Rain</span>
            <span className="t-mono" style={{ fontSize: 11 }}>61&deg;F</span>
          </Row>
        </>
      )}
      <span className="t-mono" style={{ fontSize: 11.5, color: 'var(--ink)', flex: 'none' }}>{time}</span>

      <div style={{ width: 1, height: 18, background: 'var(--line)', flex: 'none' }} />

      <Row gap={8} style={{ flex: 1, overflow: 'hidden' }}>
        <span className="t-label" style={{ color: 'var(--gold)', flex: 'none' }}>The Beat</span>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {hot === '' ? 'Waiting on the dispatch board.' : `Patrols are heavy in ${hot} this hour.`}
        </span>
      </Row>

      {!compact && (
        <Row gap={13} style={{ flex: 'none' }}>
          {TRAY.map((t) => (
            <button
              key={t.n}
              className="icon-btn"
              onClick={() => go(t.to)}
              aria-label={t.title}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
              title={t.title}
            >
              <Icon n={t.n} s={16} c="var(--ink-dim)" />
            </button>
          ))}
        </Row>
      )}
    </footer>
  )
}
